import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowUpRight } from "lucide-react";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success(t("registerTitle"));
      nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid min-h-[calc(100vh-72px)] grid-cols-1 lg:grid-cols-2">
      <aside className="relative hidden overflow-hidden border-e lg:flex" style={{ borderColor: "hsl(var(--border))", backgroundColor: "hsl(var(--ink))" }}>
        <div className="grain absolute inset-0" />
        <Logo
          size={520}
          color="hsl(var(--bg) / 0.06)"
          className="absolute -end-20 -bottom-24 pointer-events-none"
        />
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-12">
          <div className="flex items-center gap-2.5 text-[hsl(var(--bg))]">
            <Logo size={32} color="hsl(var(--bg))" />
            <div className="font-display text-lg">606MEDIADOCS</div>
          </div>
          <div>
            <h2 className="font-display text-6xl leading-[0.95] text-[hsl(var(--bg))]">
              Become a
              <br />
              <span className="font-serif text-accent">member.</span>
              <br />
              Free, forever.
            </h2>
            <p className="mt-6 max-w-sm text-sm text-[hsl(var(--bg))] opacity-70">
              {t("registerSub")}
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--bg))] opacity-50">
            §02 · register
          </div>
        </div>
      </aside>

      <div className="flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-md reveal" data-testid="register-card">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">
            02 · {t("createAccount")}
          </div>
          <h1 className="font-display mt-3 text-5xl leading-[0.95]">
            Create your <span className="font-serif text-accent">space.</span>
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <Field label={t("name")} icon={User}>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-base ps-10"
                data-testid="register-name"
              />
            </Field>
            <Field label={t("email")} icon={Mail}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base ps-10"
                data-testid="register-email"
              />
            </Field>
            <Field label={t("password")} icon={Lock}>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base ps-10"
                data-testid="register-password"
              />
            </Field>

            {err && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300" data-testid="register-error">{err}</div>}

            <button type="submit" disabled={loading} className="btn btn-accent w-full !py-3.5" data-testid="register-submit">
              {loading ? "…" : t("createAccount")}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-fg">
            {t("haveAccount")}{" "}
            <Link to="/login" className="font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:text-accent" data-testid="register-to-login">
              {t("signIn")}
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-fg">{label}</label>
      <div className="relative mt-1.5">
        {Icon && <Icon className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />}
        {children}
      </div>
    </div>
  );
}
